// Explore-stats page: Portfolio heatmap (#206).
// Centerpiece is a portfolio × sitting-week heatmap of QT question counts.
// Recharts has no native heatmap, so the grid is a small custom SVG: rows
// are portfolios (top-N by total), columns are ISO weeks, cell colour
// intensity is question count. Below it: ranked top portfolios bar, per-day
// "portfolio of the day" list, and per-portfolio average answer length.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Grid, Box, useTheme, Stack,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar } from './charts';

// Truncate long portfolio names for the heatmap row labels (e.g. "Minister
// for Infrastructure, Transport, Regional Development and Local Government"
// → "Minister for Infrastructure…"). Kept short enough that the rendered
// SVG label column fits at typical viewports.
function shortLabel(s, max = 38) {
  if (!s) return '';
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Simple SVG heatmap. rows = portfolios (top N by total), cols = weeks.
// Cell intensity is `count / max` mapped to alpha against the theme primary.
function PortfolioHeatmap({ heatmap, maxRows = 15 }) {
  const theme = useTheme();
  const data = heatmap || {};
  const weeks = data.weeks || [];
  const allPortfolios = data.portfolios || [];
  const cells = data.cells || [];

  // Reduce to the top-N portfolios so the grid stays legible.
  const portfolios = allPortfolios.slice(0, maxRows);
  const portfolioSet = new Set(portfolios);
  const lookup = useMemo(() => {
    const m = new Map();
    cells.forEach((c) => {
      m.set(`${c.portfolio}__${c.week}`, c.count);
    });
    return m;
  }, [cells]);

  const max = useMemo(() => {
    let mx = 0;
    cells.forEach((c) => {
      if (portfolioSet.has(c.portfolio) && c.count > mx) mx = c.count;
    });
    return mx || 1;
  }, [cells, portfolioSet]);

  if (!portfolios.length || !weeks.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No portfolio data yet.
      </Typography>
    );
  }

  const labelW = 260;
  const cellW = Math.max(40, Math.min(80, 600 / weeks.length));
  const cellH = 26;
  const gap = 2;
  const headerH = 28;
  const width = labelW + weeks.length * (cellW + gap);
  const height = headerH + portfolios.length * (cellH + gap);

  const base = theme.palette.primary.main;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg width={width} height={height}
           role="img"
           aria-label={`Portfolio heatmap: ${portfolios.length} portfolios `
             + `across ${weeks.length} sitting week${weeks.length === 1 ? '' : 's'}`}>
        {/* Week column headers */}
        {weeks.map((w, x) => (
          <text key={w}
                x={labelW + x * (cellW + gap) + cellW / 2}
                y={headerH - 8}
                textAnchor="middle"
                fontSize={11}
                fill={theme.palette.text.secondary}>
            {w}
          </text>
        ))}
        {/* Rows */}
        {portfolios.map((pf, y) => {
          const ry = headerH + y * (cellH + gap);
          return (
            <g key={pf}>
              <text x={labelW - 8} y={ry + cellH / 2 + 4}
                    textAnchor="end"
                    fontSize={11}
                    fill={theme.palette.text.primary}>
                <title>{pf}</title>
                {shortLabel(pf)}
              </text>
              {weeks.map((w, x) => {
                const n = lookup.get(`${pf}__${w}`) || 0;
                const alpha = n === 0 ? 0.08 : 0.25 + (n / max) * 0.75;
                return (
                  <g key={w}>
                    <rect
                      x={labelW + x * (cellW + gap)}
                      y={ry}
                      width={cellW}
                      height={cellH}
                      rx={3}
                      fill={base}
                      fillOpacity={alpha}
                      stroke={theme.palette.divider}
                    >
                      <title>{`${pf} — ${w}: ${n}`}</title>
                    </rect>
                    {n > 0 && (
                      <text x={labelW + x * (cellW + gap) + cellW / 2}
                            y={ry + cellH / 2 + 4}
                            textAnchor="middle"
                            fontSize={11}
                            fill={
                              n / max > 0.5
                                ? theme.palette.primary.contrastText
                                : theme.palette.text.primary
                            }>
                        {n}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

export default function QtPortfolios() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('qt-portfolios');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Portfolio heatmap"
      description="Which portfolios take the most Question Time questions, broken down by sitting week. Higher intensity means more questions; truncated labels show full text on hover."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="qt-portfolios"
    >
      {data && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Portfolio × sitting week
              </Typography>
              <PortfolioHeatmap heatmap={c.heatmap || {}} maxRows={15} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Top portfolios by question volume
              </Typography>
              <StatBar data={c.top || []} xKey="portfolio" yKey="count"
                       layout="vertical" height={360} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Longest average answer (chars)
              </Typography>
              <StatBar data={c.avg_answer_chars || []} xKey="portfolio"
                       yKey="chars" layout="vertical" height={360} />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Portfolio of the day
              </Typography>
              <Stack spacing={1}>
                {(c.per_day_portfolio || []).map((r, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2,
                                     borderBottom: '1px solid',
                                     borderColor: 'divider', pb: 1 }}>
                    <Box sx={{ minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
                      {r.Date}
                    </Box>
                    <Box sx={{ flex: 1 }}>{r['Top portfolio']}</Box>
                    <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                      {r.Questions} Qs
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </ExploreStatsPage>
  );
}
